import { ReactNode, FC, createContext, useState, useMemo } from 'react';
import * as types from '@models/types';

type EducationCareerContextType = {
    topEmployersByISCED4?: types.TopEmployersByISCED4Types[];
    setTopEmployersByISCED4?: React.Dispatch<React.SetStateAction<types.TopEmployersByISCED4Types[]>>;
    yearlySpecializationAndSupplyTrend?: types.YearlySpecializationAndSupplyTypes | undefined;
    setYearlySpecializationAndSupplyTrend?: React.Dispatch<React.SetStateAction<types.YearlySpecializationAndSupplyTypes | undefined>>;
    demandAndSupplyPercentageByCountry?: types.DemandAndSupplyPercentageByCountryTypes | undefined;
    setDemandAndSupplyPercentageByCountry?: React.Dispatch<React.SetStateAction<types.DemandAndSupplyPercentageByCountryTypes | undefined>>;
    topSectorAndOccupationByISCED4?: types.SectorAndISCO3AndISCO4OccupationTypes | undefined;
    setTopSectorAndOccupationByISCED4?: React.Dispatch<React.SetStateAction<types.SectorAndISCO3AndISCO4OccupationTypes | undefined>>;
    careerPathwayByISCED4?: types.CareerPathwayByISCED4Types[];
    setCareerPathwayByISCED4?: React.Dispatch<React.SetStateAction<types.CareerPathwayByISCED4Types[]>>;
    educationLevelPercentage?: types.EducationLevelPercentageTypes[];
    setEducationLevelPercentage?: React.Dispatch<React.SetStateAction<types.EducationLevelPercentageTypes[]>>;
    supplyEducationLevelPercentage?: types.EducationLevelPercentageTypes[];
    setSupplyEducationLevelPercentage?: React.Dispatch<React.SetStateAction<types.EducationLevelPercentageTypes[]>>;
    totalGraduatesAndStudent?: types.TotalGraduatesAndStudents;
    setTotalGraduatesAndStudent?: React.Dispatch<React.SetStateAction<types.TotalGraduatesAndStudents>>;
    supplyOfGraduatesByISCED4?: types.SupplyOfGraduatesByISCED4Types;
    setSupplyOfGraduatesByISCED4?: React.Dispatch<React.SetStateAction<types.SupplyOfGraduatesByISCED4Types>>;
    topSpecializationsBySupply?: types.TopSpecializationsBySupplyTypes[];
    setTopSpecializationsBySupply?: React.Dispatch<React.SetStateAction<types.TopSpecializationsBySupplyTypes[]>>;
    topSpecializationsBySupplyJobPostView?: types.TopSpecializationsBySupplyJobPostViewTypes[];
    setTopSpecializationsBySupplyJobPostView?: React.Dispatch<React.SetStateAction<types.TopSpecializationsBySupplyJobPostViewTypes[]>>;
    topSpecializationsBySupplyGraduatesView?: types.TopSpecializationsBySupplyGraduatesViewTypes[];
    setTopSpecializationsBySupplyGraduatesView?: React.Dispatch<React.SetStateAction<types.TopSpecializationsBySupplyGraduatesViewTypes[]>>;
    topSpecializationsByDemand?: types.TopSpecializationsByDemandTypes[];
    setTopSpecializationsByDemand?: React.Dispatch<React.SetStateAction<types.TopSpecializationsByDemandTypes[]>>;
    topSpecializationsByDemandJobPostView?: types.TopSpecializationsByDemandJobPostViewTypes[];
    setTopSpecializationsByDemandJobPostView?: React.Dispatch<React.SetStateAction<types.TopSpecializationsByDemandJobPostViewTypes[]>>;
    topSpecializationsByDemandGraduatesView?: types.TopSpecializationsByDemandGraduatesViewTypes[];
    setTopSpecializationsByDemandGraduatesView?: React.Dispatch<React.SetStateAction<types.TopSpecializationsByDemandGraduatesViewTypes[]>>;
};

const EducationCareerContext = createContext<EducationCareerContextType>({});


type EducationCareerProps = {
  children: ReactNode;
};

const EducationCareer: FC<EducationCareerProps> = ({ children }) => {
    const [topEmployersByISCED4, setTopEmployersByISCED4] = useState<types.TopEmployersByISCED4Types[]>([]);
    const [yearlySpecializationAndSupplyTrend, setYearlySpecializationAndSupplyTrend] = useState<types.YearlySpecializationAndSupplyTypes | undefined>();
    const [demandAndSupplyPercentageByCountry, setDemandAndSupplyPercentageByCountry] = useState<types.DemandAndSupplyPercentageByCountryTypes | undefined>();
    const [topSectorAndOccupationByISCED4, setTopSectorAndOccupationByISCED4] = useState<types.SectorAndISCO3AndISCO4OccupationTypes | undefined>();
    const [careerPathwayByISCED4, setCareerPathwayByISCED4] = useState<types.CareerPathwayByISCED4Types[]>([]);
    const [educationLevelPercentage, setEducationLevelPercentage] = useState<types.EducationLevelPercentageTypes[]>([]);
    const [supplyEducationLevelPercentage, setSupplyEducationLevelPercentage] = useState<types.EducationLevelPercentageTypes[]>([]);
    const [totalGraduatesAndStudent, setTotalGraduatesAndStudent] = useState<types.TotalGraduatesAndStudents>({ institutionTypeTotalGraduates: [] });
    const [supplyOfGraduatesByISCED4, setSupplyOfGraduatesByISCED4] = useState<types.SupplyOfGraduatesByISCED4Types>({ topFiveSupply: [], total: null });
    const [topSpecializationsBySupply, setTopSpecializationsBySupply] = useState<types.TopSpecializationsBySupplyTypes[]>([]);
    const [topSpecializationsBySupplyJobPostView, setTopSpecializationsBySupplyJobPostView] = useState<types.TopSpecializationsBySupplyJobPostViewTypes[]>([]);
    const [topSpecializationsBySupplyGraduatesView, setTopSpecializationsBySupplyGraduatesView] = useState<types.TopSpecializationsBySupplyGraduatesViewTypes[]>([]);
    const [topSpecializationsByDemand, setTopSpecializationsByDemand] = useState<types.TopSpecializationsByDemandTypes[]>([]);
    const [topSpecializationsByDemandJobPostView, setTopSpecializationsByDemandJobPostView] = useState<types.TopSpecializationsByDemandJobPostViewTypes[]>([]);
    const [topSpecializationsByDemandGraduatesView, setTopSpecializationsByDemandGraduatesView] = useState<types.TopSpecializationsByDemandGraduatesViewTypes[]>([]);


    const EducationCareerProviderValue = useMemo(
        () => ({
            topEmployersByISCED4, setTopEmployersByISCED4,
            yearlySpecializationAndSupplyTrend, setYearlySpecializationAndSupplyTrend,
            demandAndSupplyPercentageByCountry, setDemandAndSupplyPercentageByCountry,
            topSectorAndOccupationByISCED4, setTopSectorAndOccupationByISCED4,
            careerPathwayByISCED4, setCareerPathwayByISCED4,
            educationLevelPercentage, setEducationLevelPercentage,
            supplyEducationLevelPercentage, setSupplyEducationLevelPercentage,
            totalGraduatesAndStudent, setTotalGraduatesAndStudent,
            supplyOfGraduatesByISCED4, setSupplyOfGraduatesByISCED4,
            topSpecializationsBySupply, setTopSpecializationsBySupply,
            topSpecializationsBySupplyJobPostView, setTopSpecializationsBySupplyJobPostView,
            topSpecializationsBySupplyGraduatesView, setTopSpecializationsBySupplyGraduatesView,
            topSpecializationsByDemand, setTopSpecializationsByDemand,
            topSpecializationsByDemandJobPostView, setTopSpecializationsByDemandJobPostView,
            topSpecializationsByDemandGraduatesView, setTopSpecializationsByDemandGraduatesView
        }),
        [
            topEmployersByISCED4, setTopEmployersByISCED4,
            yearlySpecializationAndSupplyTrend, setYearlySpecializationAndSupplyTrend,
            demandAndSupplyPercentageByCountry, setDemandAndSupplyPercentageByCountry,
            topSectorAndOccupationByISCED4, setTopSectorAndOccupationByISCED4,
            careerPathwayByISCED4, setCareerPathwayByISCED4,
            educationLevelPercentage, setEducationLevelPercentage,
            supplyEducationLevelPercentage, setSupplyEducationLevelPercentage,
            totalGraduatesAndStudent, setTotalGraduatesAndStudent,
            supplyOfGraduatesByISCED4, setSupplyOfGraduatesByISCED4,
            topSpecializationsBySupply, setTopSpecializationsBySupply,
            topSpecializationsBySupplyJobPostView, setTopSpecializationsBySupplyJobPostView,
            topSpecializationsBySupplyGraduatesView, setTopSpecializationsBySupplyGraduatesView,
            topSpecializationsByDemand, setTopSpecializationsByDemand,
            topSpecializationsByDemandJobPostView, setTopSpecializationsByDemandJobPostView,
            topSpecializationsByDemandGraduatesView, setTopSpecializationsByDemandGraduatesView
        ]
  );

  return (
    <EducationCareerContext.Provider value={EducationCareerProviderValue}>
      {children}
    </EducationCareerContext.Provider>
  );
};

export { EducationCareer, EducationCareerContext };